// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
// ── Regulatory Frameworks ────────────────────────────────────────
/**
 * Sprint 64.65.0 — derived from the corpus, and pinned to it by test.
 *
 * This union declared five values while `packs/regulatory` + `packs/market`
 * carried **eight**, so a developer writing a status allowlist against the type
 * was wrong before they started. That is not hypothetical: Sprint 64.8.0's
 * capability registry shipped `new Set(['active', 'upcoming'])` and would have
 * dropped five live values including `temporary_active`, which two filing paths
 * depend on. The defect was caught at review; the type that invited it was not.
 *
 * Counts measured 2026-08-17 across 202 framework records:
 *
 *   active 185 · draft 5 · upcoming 3 · legacy 3 · active_reference 3
 *   reference_verification_required 1 · temporary_active 1
 *   draft_enforcement_overlay 1
 *
 * **`draft_enforcement_overlay` is removed (2026-08-24).** Its one record —
 * the shared cyber + PDPL enforcement draft — moved to `legacy` when Decree
 * 330/2026/NĐ-CP promulgated it, so the corpus-parity test fails DIRECTION 2
 * on it, the same way `superseded` was retired below. If a future draft
 * overlay ships with the status, the test fails and it gets re-added
 * deliberately.
 *
 * **`reference_verification_required` is removed (2026-08-25).** Its one record
 * — the Decree 98/2020 consumer-protection sanctions reference — moved to
 * `active` once its current status was verified against the Government
 * legal-document portal: Decree 24/2025/NĐ-CP amends and supplements Decree
 * 98/2020 rather than replacing it, so the instrument is in force in its
 * consolidated form. No pack writes the status now, so the corpus-parity test
 * fails DIRECTION 2 on it, the same way `draft_enforcement_overlay` and
 * `superseded` were retired. If a future intake ships an unverified reference,
 * the test fails and it gets re-added deliberately.
 *
 * Counted at the FRAMEWORK level only. A `grep` for `"status"` also matches
 * `requirements[].status` (31 records, a different union) and reports values
 * that are not framework statuses at all — `official_roadmap` among them. The
 * corpus test below parses the JSON and reads `frameworks[].status`, which is
 * why it caught that error rather than inheriting it.
 *
 * The corpus is authoritative here rather than the type: every undeclared value
 * is real pack content with real consumers, and "correct the corpus" would mean
 * editing regulatory content to suit a TypeScript union — a Compliance decision,
 * not a typing one.
 *
 * **`superseded` is removed.** It was declared and used by **zero** records.
 * Supersession is expressed on a framework by `replaced_by` (3 records), which
 * `regulatory-index.ts` reads into `superseded_by`. A status value nothing
 * writes is the same shape `ai-governance-service.ts:1104` records finding in
 * its own review-status union. If a pack ever needs it, the corpus-parity test
 * fails and it gets added deliberately.
 *
 * `regulatory-framework-status-corpus-64.65.0.test.ts` reads the packs and fails
 * in BOTH directions — a corpus value this union does not admit, and a union
 * member the corpus declares nowhere. A strict superset rots exactly as
 * silently as a strict subset.
 *
 * Exported as a value array first, with the union derived from it, for the same
 * reason `MAPPING_TYPES` is: the write path has to be able to validate against
 * the vocabulary rather than restate it. Two hand-copied allowlists had already
 * drifted from this union in opposite directions at once — one rejecting four
 * statuses the corpus carries (`active_reference`, `temporary_active`,
 * `reference_verification_required`, `draft_enforcement_overlay`) while
 * accepting two it does not (`superseded`, `archived`), the other scoring
 * coverage over a narrower population than its own docblock claimed. A
 * developer writing a status allowlist against the type was wrong before they
 * started; now there is nothing to write.
 */
export const REGULATORY_FRAMEWORK_STATUSES = [
    'active',
    'active_reference',
    'draft',
    'legacy',
    'temporary_active',
    'upcoming',
];
/**
 * The statuses that mean **the instrument is in force today**.
 *
 * `active` is not the whole of "in force", and reading it as though it were is
 * what made the Regulatory Frameworks KPI row unable to add up: four cards —
 * Active / Draft / Upcoming / Legacy — over a six-member vocabulary, with
 * `active_reference` and `temporary_active` falling through every one of them.
 * A customer counting a 119-framework corpus got 114 and no card for the rest.
 *
 * - `active` — in force, a direct obligation source.
 * - `active_reference` — in force as reference or strategy material rather than
 *   a direct obligation source. Still current law; still counts as in force.
 * - `temporary_active` — in force for a stated window (`effective_until`). The
 *   NQ22/2026 administrative-simplification overlay is the live instance.
 *
 * Deliberately NOT here: `draft` and `upcoming` are not yet in force, `legacy`
 * no longer is. Together with those three, this list partitions
 * `REGULATORY_FRAMEWORK_STATUSES` exactly — asserted below, so a status added
 * to the vocabulary without being classified fails the build rather than
 * silently vanishing from a count.
 *
 * This is the vocabulary itself rather than a hand-copied allowlist, for the
 * reason the docblock above gives: two such allowlists had already drifted from
 * this union in opposite directions at once.
 */
export const IN_FORCE_FRAMEWORK_STATUSES = [
    'active',
    'active_reference',
    'temporary_active',
];
/** The statuses that are NOT in force — the other half of the partition. */
export const NOT_IN_FORCE_FRAMEWORK_STATUSES = [
    'draft',
    'upcoming',
    'legacy',
];
const _assertPartition = [true, true];
void _assertPartition;
/**
 * Statuses this vocabulary has retired, and what each record carrying one
 * became — Sprint 64.164.0.
 *
 * Retiring a status from the union does not retire it from a database. The
 * three entries below were each removed after their last record moved
 * elsewhere (see the docblock on `REGULATORY_FRAMEWORK_STATUSES`), and every
 * one of those moves was a change to PACK CONTENT. A tenant that installed the
 * pack beforehand still holds the old value, and until its pack upgrades it
 * will keep holding it.
 *
 * That is not hypothetical either. `reference_verification_required` was
 * retired on 2026-08-25 when Decree 98/2020 was verified in force and its
 * record moved to `active`; the live Vietnam catalogue still carried the
 * retired value the next day, because the pack it lives in is opt-in and no
 * install path refreshed it. The Regulatory Frameworks KPI row then summed to
 * 118 of 119 — one framework in no card, and nothing said so.
 *
 * The successor for each is read from that history, not guessed:
 *
 * - `reference_verification_required` → `active`. The status meant "in force,
 *   pending source verification". Verification confirmed in force.
 * - `draft_enforcement_overlay` → `legacy`. Its record moved to `legacy` when
 *   Decree 330/2026/NĐ-CP promulgated the draft it stood for.
 * - `superseded` → `legacy`. Mapped because two consumers special-cased the
 *   string by hand, which is the drift this map exists to end.
 *
 *   This entry used to add "Never written by any pack; supersession is expressed
 *   by `replaced_by`." That was false when written and stayed false for three
 *   weeks: `vn-cybersecurity/frameworks.json` wrote it on five
 *   `framework_stack` rows, and the sentence held because the claim was about
 *   framework RECORDS while the value was being written on ROWS — a field this
 *   map's author had no reason to look at, since `status: string` gave rows no
 *   vocabulary to belong to. fix-sprint-20260912-1003 gave them one
 *   (`FRAMEWORK_STACK_ROW_STATUSES`), and row statuses are read into this
 *   vocabulary by `stackRowStatusToRecordStatus`, not by this map. Corrected
 *   rather than deleted, because the shim itself is still right: a record found
 *   carrying `superseded` is a record that is `legacy`.
 *
 * This is a READ-side compatibility shim and deliberately not a substitute for
 * repairing the data: `pack-install-engine.ts` now refreshes an installed
 * opt-in pack so the stored value is corrected at its source. The shim means a
 * count is right the moment the page loads rather than the moment an admin
 * remembers to reinstall.
 */
export const RETIRED_FRAMEWORK_STATUS_SUCCESSORS = {
    reference_verification_required: 'active',
    draft_enforcement_overlay: 'legacy',
    superseded: 'legacy',
};
/**
 * A stored status resolved to a member of the current vocabulary.
 *
 * Returns `null` for a value that is neither current nor a known retirement —
 * a genuinely unknown status, which a caller must handle rather than count as
 * anything. Returning `null` instead of a default is the point: guessing a
 * bucket for an unknown value is how the count silently drifted in the first
 * place.
 */
export function normalizeFrameworkStatus(status) {
    if (!status)
        return null;
    if (REGULATORY_FRAMEWORK_STATUSES.includes(status)) {
        return status;
    }
    return RETIRED_FRAMEWORK_STATUS_SUCCESSORS[status] ?? null;
}
/*
 * Sprint 62.27.0 — `frameworkDutiesAreOwed` moved here from the backend.
 *
 * It was declared in `src/backend/src/modules/localization/
 * framework-capability-registry.ts` and is a pure predicate over a stored
 * status string whose only dependency, `normalizeFrameworkStatus`, is two
 * functions above. Ten `src/shared` type docblocks already cite it by name as
 * the answer their field carries, so shared was already where it belonged.
 *
 * The move was forced rather than chosen: this sprint's `expandInstalledStack`
 * lives in `src/shared` and must drop rows whose duties are not owed, and
 * shared may not import from backend (R-01). The backend registry re-exports
 * this symbol, so all of its existing importers are unchanged.
 *
 * Sprint 62.49.0 (AC29) — `suspended` joins `legacy` and `draft`. It is not a
 * member of `REGULATORY_FRAMEWORK_STATUSES` (that vocabulary decision is the
 * owner's, AC-B2 / #459) and no installed pack carries it today. It is listed
 * so the predicate fails CLOSED for it ahead of that decision: a framework
 * whose application is suspended owes no duty, and a later change that writes
 * `suspended` onto a record must not — by falling through to the unknown-value
 * branch below — make the suspended duties owed again without a deliberate
 * ruling. Only this one named value is added. The unknown-status rule is
 * unchanged: any other value nobody has seen still counts as owed, and
 * `__tests__/framework-duties-owed-62.49.0.test.ts` asserts both halves.
 */
const DUTIES_NOT_OWED_STATUSES = new Set(['legacy', 'draft', 'suspended']);
/**
 * Whether an installed framework's duties are still owed today.
 *
 * A status nobody has seen before **counts**, deliberately: a new value
 * arriving from a pack is a content question, and silently dropping every
 * framework carrying it is the defect this function was written to remove.
 * `?? status` preserves that — `normalizeFrameworkStatus` returns null for an
 * unknown value and the raw string is then tested against the set.
 *
 * `installed-pack-scope.ts` holds a NARROWER answer for a different question
 * (may this ground an AI draft) and the two are deliberately not merged; what
 * must not happen is a third list appearing because neither was reachable.
 */
export function frameworkDutiesAreOwed(status) {
    if (status === undefined || status === null)
        return true;
    const resolved = normalizeFrameworkStatus(status) ?? status;
    return !DUTIES_NOT_OWED_STATUSES.has(resolved);
}
export function notOwedLifecycle(status) {
    if (normalizeFrameworkStatus(status) === 'legacy')
        return 'legacy';
    if (status === 'suspended')
        return 'suspended';
    return 'draft';
}
/**
 * Whether an installed framework's instrument is in force today.
 *
 * Normalises first, so a record still carrying a retired status is read as
 * whatever that status became rather than falling out of every bucket.
 */
export function isFrameworkInForce(status) {
    const resolved = normalizeFrameworkStatus(status);
    return resolved !== null && IN_FORCE_FRAMEWORK_STATUSES.includes(resolved);
}
/*
 * Sprint 62.49.1 (DB-2, AC-B2/AC-B4) — a deadline row's OWN `status` that the
 * pack states makes the row owe nothing, whatever its framework's status.
 *
 * Promoted from the food module (`FOOD_TRACEABILITY_INOPERATIVE_DEADLINE_ROW_
 * STATUSES`, Sprint 62.49.0 D16, which listed `suspended` and `superseded`) so
 * the food records clock and the deadline reads of the localization module ask
 * one list (R-43). `legacy` joins them: two installed rows carry it today
 * (`vn-data-law`, under a `legacy` framework), and a row the pack calls legacy
 * is no more owed than a legacy framework (`DUTIES_NOT_OWED_STATUSES`). A
 * status nobody has seen counts as operative, the stance
 * `frameworkDutiesAreOwed` takes for a framework.
 */
export const INOPERATIVE_DEADLINE_ROW_STATUSES = ['suspended', 'superseded', 'legacy'];
/**
 * Sprint 62.49.1 (DB-2) — why a deadline row owes no duty. The row's own status
 * is the more specific statement and is read first (`row_superseded`,
 * `row_suspended`, `row_legacy`); `framework_status` is a row of an operative
 * status under a framework whose duties are not owed (`frameworkDutiesAreOwed`
 * false: `legacy`, `draft`, `suspended`).
 */
export const DEADLINE_DUTY_NOT_OWED_REASONS = [
    'framework_status',
    'row_suspended',
    'row_superseded',
    'row_legacy',
];
const ROW_STATUS_REASON = {
    superseded: 'row_superseded',
    suspended: 'row_suspended',
    legacy: 'row_legacy',
};
/**
 * Sprint 62.49.1 (DB-2, AC-B2/AC-B4) — whether a deadline row owes no duty, and
 * why; `null` when it is owed (or nothing says otherwise).
 *
 * Read on the server from the declaring framework's `status` and the row's own
 * `status` and `superseded_by_framework_code`; the activation the resolver
 * computes is left as it is (a suspended row's framework may well have taken
 * effect), and a surface that would print *In force* reads this first.
 * `frameworkStatus` is `undefined` when no framework record was read: the row's
 * own status still decides, and otherwise nothing is decided here (the
 * activation state already says the record was unreadable) — an absent status
 * counts as owed, as `frameworkDutiesAreOwed` reads it.
 */
export function deadlineDutyNotOwed(frameworkStatus, row) {
    const { status, superseded_by_framework_code: code } = row;
    const own = typeof status === 'string' ? status : null;
    if (own !== null && Object.prototype.hasOwnProperty.call(ROW_STATUS_REASON, own)) {
        const reason = ROW_STATUS_REASON[own];
        return {
            reason,
            superseded_by_framework_code: reason === 'row_superseded' && typeof code === 'string' && code.trim().length > 0
                ? code.trim()
                : null,
        };
    }
    if (!frameworkDutiesAreOwed(frameworkStatus)) {
        return { reason: 'framework_status', superseded_by_framework_code: null };
    }
    return null;
}
/**
 * Runtime twin of `ApplicableModule` — Sprint 62.19.0 follow-up.
 *
 * `ModuleMappingsPage.tsx` used to restate the union as a hand list of eight
 * values; it lacked `vendor`, `risk` and the five sector-instrument modules
 * above, so a pack-installed mapping on any of those seven rendered the
 * `*`-marked required select with nothing selected (the `most_restrictive`
 * defect of 64.149.0, again, on the other select). The union itself cannot be
 * mapped over at runtime, so this array exists — same pattern as
 * `MAPPING_TYPES` / `CONFLICT_RESOLUTIONS` below.
 *
 * The union above is deliberately NOT re-declared as
 * `(typeof APPLICABLE_MODULES)[number]`: two corpus gates read its members out
 * of this source text with a lazy regex on the literal union body (see the
 * comment above it). `satisfies` proves array ⊆ union at compile time;
 * `src/shared/src/types/__tests__/applicable-modules-62.19.0.test.ts` proves
 * union ⊆ array with the same extraction the gates use.
 */
export const APPLICABLE_MODULES = [
    'consent',
    'dpia',
    'incident',
    'rights_request',
    'data_mapping',
    'classification',
    'monitoring',
    'governance',
    'vendor',
    'risk',
    'sector_licensing',
    'investor_verification',
    'account_data_governance',
    'regulatory_disclosure',
    'authority_direction',
    'issued_record',
    'publication_assessment',
    'legal_deposit',
    'legal_representative',
];
// ── Regulatory Mappings ──────────────────────────────────────────
/**
 * Sprint 64.149.0 gate E12.5 — realigned to the values the platform actually
 * writes and renders.
 *
 * The previous union was `legal_basis | rights_type | notification_timeline |
 * assessment_section | record_field | classification_label |
 * incident_reporting_timeline | supply_chain_requirement | governance_measure`.
 * No code in this repository ever produced, consumed, or referenced one of
 * those nine values: `MappingType` had no usage outside its own declaration,
 * `RegulatoryMappingService.seedFromFrameworks()` writes `'direct'`, and
 * `ModuleMappingsPage.tsx` offers, filters on, and badges exactly the five
 * below. A union that names nine values none of which any writer produces is
 * not a contract; it is a comment that `tsc` cannot check.
 *
 * `MAPPING_TYPES` / `CONFLICT_RESOLUTIONS` are exported as value arrays so the
 * backend zod enum and the frontend `<select>` derive from this declaration
 * instead of restating it. That restatement is what let the three surfaces
 * drift apart in the first place.
 */
export const MAPPING_TYPES = ['direct', 'partial', 'derived', 'equivalent', 'custom'];
/**
 * `most_restrictive` is not offered by the create form — it is what
 * `db/pack-installer.ts` stamps on every mapping it installs from a pack. It is
 * in the union because the edit form has to be able to round-trip a
 * pack-installed row without silently rewriting its conflict rule.
 */
export const CONFLICT_RESOLUTIONS = [
    'override',
    'merge',
    'manual',
    'strict',
    'most_restrictive',
];
/**
 * The legislative layers a framework stack may declare.
 *
 * Exported as a value array so `validate:packs` reads the vocabulary out of
 * this declaration rather than carrying a seventh copy of it — the
 * `MAPPING_TYPES` / `REGULATORY_FRAMEWORK_STATUSES` pattern already used twice
 * in this file, and the reason 64.13.0 needed a guard test for
 * `SUBMISSION_CHANNEL_TYPES`: a copy of a union is how the gate and the
 * contract come to disagree about the same content.
 *
 * ## Sprint 64.42.0 — `prime_minister_decision`
 *
 * Added because the corpus already carried it. The first run of this sprint's
 * `FRAMEWORK_STACK_LAYER_TYPE_INVALID` rule over both pack roots reported
 * exactly one violation: `vn-ai-law / VN_AI_LAW_2026` declares a layer for
 * Decision 33/2026/QĐ-TTg with `layer_type: 'prime_minister_decision'`.
 *
 * Triaged as a **type defect, not a content defect**, per the locked decision
 * that a rule which rejects existing valid content is a defect in the rule: a
 * Prime Minister's decision (`QĐ-TTg`) is a distinct instrument class from a
 * government decree (`NĐ-CP`), and the six values here had no member for it, so
 * the pack author had nowhere correct to put it. Coercing it to
 * `implementing_decree` would delete a distinction the legislative hierarchy
 * draws.
 *
 * This is 62.1.0's `FrameworkTypeSchema` widening (11 → 19 values, for content
 * that had shipped and never been validated) one interface over, found the same
 * way: by running a rule over content for the first time.
 *
 * No runtime behaviour changes. `FrameworkDetailView.tsx`'s `layerLabel` takes
 * a `string` and falls back to a humanised form of the raw value, so the seventh
 * member has rendered correctly since the pack shipped; what was missing was
 * the type admitting it and the gate checking it.
 */
export const FRAMEWORK_STACK_LAYER_TYPES = [
    'parent_law',
    'implementing_decree',
    'prime_minister_decision',
    'enforcement_decree',
    'admin_procedures',
    'official_roadmap',
    /**
     * fix-sprint-20260912-1003 F13 — the layer a `child_frameworks` row lands in.
     *
     * No pack authors this value and none should: it is what a child row becomes
     * when it is presented as a stack row. `child_frameworks` carries
     * `relationship` instead of `layer_type`, and that is an OPEN 24-value
     * vocabulary (`decree`, `technical_circular`, `national_technical_regulation`,
     * `shared_draft_enforcement_overlay`, …) which does not map onto the six
     * legislative layers above without inventing a classification per value.
     *
     * So derived rows share one group and keep `relationship` visible on the row.
     * The alternative — deriving a layer per relationship — would have produced
     * 24 single-row groups and asserted legislative classifications no pack made.
     */
    'child_instrument',
];
/**
 * fix-sprint-20260912-1003 F12 — `enforcement_overlay` is retired into
 * `enforcement_decree`.
 *
 * ## Why it was redundant, measured
 *
 * The corpus carried four enforcement rows and they named **two instruments**:
 *
 *   vn-pdpl        VN_PDPL_LAW_2025      enforcement_decree   VN_CYBERSEC_PDPL_PENALTIES_DECREE_330_2026
 *   vn-pdpl        VN_PDPL_LAW_2025      enforcement_decree   VN_CYBER_PDPL_ENFORCEMENT_DRAFT_2026
 *   vn-cybersecurity  VN_CYBERSEC_LAW_2025  enforcement_overlay  VN_CYBERSEC_PDPL_PENALTIES_DECREE_330_2026
 *   vn-cybersecurity  VN_CYBERSEC_LAW_2025  enforcement_overlay  VN_CYBER_PDPL_ENFORCEMENT_DRAFT_2026
 *
 * The same two instruments, typed differently by which parent's stack they
 * appeared under. Decree 330/2026/NĐ-CP is an enforcement decree in both — it
 * does not change kind because a different law points at it — so the page
 * headed one group "Enforcement Decree" and the other "Enforcement Overlay"
 * over identical content.
 *
 * `enforcement_decree` survives because every other member of this vocabulary
 * names what the instrument **is** — `parent_law`, `implementing_decree`,
 * `prime_minister_decision`, `admin_procedures`. `enforcement_overlay` named a
 * *relationship*, which is already carried by the row's position in a parent's
 * stack, and was the only member doing so.
 *
 * Kept as a shim rather than deleted, for the same reason
 * `RETIRED_FRAMEWORK_STATUS_SUCCESSORS` exists: a pack authored before this
 * must normalise rather than fall through a renderer's default. `validate:packs`
 * reports a row still declaring a retired value (STACK_LAYER_TYPE_RETIRED), so
 * the shim cannot quietly become the permanent answer.
 */
export const RETIRED_FRAMEWORK_STACK_LAYER_TYPES = {
    enforcement_overlay: 'enforcement_decree',
};
/** Resolve a declared layer type, normalising a retired value. Returns `null`
 *  for a value in neither vocabulary — the caller decides, rather than this
 *  guessing a group for a row nobody has classified. */
export function normalizeFrameworkStackLayerType(value) {
    if (typeof value !== 'string')
        return null;
    if (FRAMEWORK_STACK_LAYER_TYPES.includes(value)) {
        return value;
    }
    return RETIRED_FRAMEWORK_STACK_LAYER_TYPES[value] ?? null;
}
/**
 * fix-sprint-20260912-1003 — the stack row's own status vocabulary.
 *
 * ## Why this exists at all
 *
 * `FrameworkStackLayer.status` was declared `string`. It was the only framework
 * status field in the product with no vocabulary: `RegulatoryFrameworkStatus`
 * beside it is a closed six-value union with a compile-time partition assertion,
 * a retirement shim and a corpus test, while the endpoint that writes a stack row
 * validated `if (!status)` and nothing else. Three of the five statuses the corpus
 * actually used — `official_roadmap`, `superseded`, `draft_enforcement_overlay` —
 * were outside the record vocabulary, and the renderer's badge was a binary
 * (`isActive ? Active : Draft`) with no third branch, so a replaced draft could
 * only render as "Active", as "Draft", or as nothing at all.
 *
 * ## Two vocabularies, deliberately
 *
 * A row is a PRESENTATION of an instrument inside one parent's stack; a record is
 * the instrument. They answer different questions, so `legacy` is **not** a row
 * status: a row must say *how* an instrument stopped being operative, which is the
 * distinction the product owner drew —
 *
 * > A draft is not superseded. [...] A draft that was promulgated was never in
 * > force, so it was never superseded; it *became* the instrument.
 *
 * Hence `promulgated` and `superseded` as separate row states. `promulgated` is not
 * new: Sprint 25.5.0.2 shipped the "Mark promulgated" button that writes it and the
 * pill that renders it, and the packs wrote `superseded` instead, falling through
 * both branches.
 *
 * The in-force and pending half is DERIVED from `RegulatoryFrameworkStatus` rather
 * than retyped, so a status added to the record vocabulary surfaces here as a
 * compile error instead of silently not being renderable.
 * `framework-capability-registry.ts` warns that "what must not happen is a fourth
 * list appearing"; this is not one.
 */
export const FRAMEWORK_STACK_ROW_ONLY_STATUSES = [
    /** Was a draft; enacted as the instrument named in `lineage`. Lineage, hidden by default. */
    'promulgated',
    /** Was in force; replaced by the instrument named in `lineage`. Lineage, hidden by default. */
    'superseded',
    /** A plan or roadmap with no framework record of its own. Never a link. */
    'official_roadmap',
];
/**
 * The runtime list, asserted below to be exactly `FrameworkStackRowStatus`.
 *
 * A literal array is unavoidable for validation, so the assertion is what keeps it
 * honest: adding a member to `REGULATORY_FRAMEWORK_STATUSES` or to
 * `FRAMEWORK_STACK_ROW_ONLY_STATUSES` without adding it here fails the build.
 */
export const FRAMEWORK_STACK_ROW_STATUSES = [
    'active',
    'active_reference',
    'temporary_active',
    'upcoming',
    'draft',
    'promulgated',
    'superseded',
    'official_roadmap',
];
const _assertStackRowStatuses = [true];
void _assertStackRowStatuses;
/** The two row states that describe an instrument that is no longer operative. */
export const FRAMEWORK_STACK_LINEAGE_STATUSES = ['promulgated', 'superseded'];
/** Whether a stored row status is one this vocabulary admits. */
export function isFrameworkStackRowStatus(status) {
    return (typeof status === 'string' &&
        FRAMEWORK_STACK_ROW_STATUSES.includes(status));
}
/**
 * A row status read in the RECORD vocabulary, so a guard can compare the two.
 *
 * Deliberately separate from `RETIRED_FRAMEWORK_STATUS_SUCCESSORS`, which maps
 * retired *record* statuses forward. Overloading that map with row states would
 * blur the two vocabularies this sprint exists to separate — and it is why
 * `findInstrumentStatusDisagreements` documented "NOT checked: status equality,
 * because rows and frameworks use two vocabularies". With this, it can be.
 *
 * `official_roadmap` returns `null`: those rows resolve to no framework record at
 * all, which is correct and is not a disagreement.
 */
export function stackRowStatusToRecordStatus(status) {
    switch (status) {
        case 'promulgated':
        case 'superseded':
            return 'legacy';
        case 'official_roadmap':
            return null;
        default:
            return normalizeFrameworkStatus(status ?? null);
    }
}
// ── Framework Transition Review ──────────────────────────────────
// General-purpose mechanism for tracking regulatory framework succession reviews.
// When a framework has `replaced_by` set and the successor has an `effective_date`,
// the system can auto-derive `review_by` as `effective_date - lead_time_days`.
/**
 * Exported as a value array for the same reason as
 * `FRAMEWORK_STACK_LAYER_TYPES` above — Sprint 64.42.0's pack-time rule for
 * `transition_review` reads this vocabulary rather than restating it.
 */
export const TRANSITION_REVIEW_STATUSES = ['pending', 'in_progress', 'completed'];
//# sourceMappingURL=localization.js.map